import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  ChartBarIcon,
  CpuChipIcon,
  BeakerIcon,
  ExclamationTriangleIcon,
  DocumentChartBarIcon,
  CogIcon,
  BoltIcon,
  EyeIcon,
  CircleStackIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  UserCircleIcon,
  Bars3Icon,
  CloudIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import ThemeToggle from '../ThemeToggle';
import { authService } from '../../services/api/auth';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobileOpen?: boolean;
  onMobileToggle?: () => void;
}

const AISidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle, isMobileOpen, onMobileToggle }) => {
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const navigationSections = [
    {
      title: 'Overview',
      items: [
        { id: 'live-24-7', label: '24/7 Dashboard', icon: CircleStackIcon, path: '/live/24-7', badge: '24/7' },
        { id: 'overview', label: 'Overview', icon: HomeIcon, path: '/overview' },
        { id: 'dashboard', label: 'Dashboard', icon: ChartBarIcon, path: '/dashboard', badge: 'Live' },
        { id: 'technical-dashboard', label: 'Technical Dashboard', icon: DocumentChartBarIcon, path: '/technical-dashboard' },
        { id: 'supervisory-dashboard', label: 'Supervisory Dashboard', icon: EyeIcon, path: '/supervisory-dashboard' },
        { id: 'fault-intelligence', label: 'Fault Intelligence', icon: ExclamationTriangleIcon, path: '/fault-intelligence', badge: 'Live' },
      ]
    },
    {
      title: 'Performance Analytics',
      items: [
        { id: 'block-details', label: 'Block Performance', icon: CpuChipIcon, path: '/block-details' },
        { id: 'inverter-metrics', label: 'Inverter Metrics', icon: BoltIcon, path: '/inverter-metrics' },
        { id: 'power-heatmap', label: 'Power Heatmap', icon: ChartBarIcon, path: '/power-heatmap', badge: 'Live' },
        { id: 'inverter-trend-analysis', label: 'Trend Analysis', icon: ChartBarIcon, path: '/inverter-trend-analysis' },
        { id: 'yield-comparison', label: 'Yield Benchmarking', icon: ChartBarIcon, path: '/yield-comparison' },
        { id: 'block-generation', label: 'Block Generation RMS', icon: ChartBarIcon, path: '/block-generation', badge: 'RMS' },
      ]
    },
    {
      title: 'Environmental Diagnostics',
      items: [
        { id: 'weather-diagnostics', label: 'Weather Comparison', icon: CloudIcon, path: '/weather-diagnostics', badge: 'Live' },
      ]
    },
    {
      title: 'Live Monitoring',
      items: [
        { id: 'live-monitoring', label: 'Real-time Monitor', icon: EyeIcon, path: '/live/monitoring', badge: 'Live' },
      ]
    },
    {
      title: 'AI & ML',
      items: [
        { 
          id: 'ai-predictions', 
          label: 'Predictions', 
          icon: CpuChipIcon, 
          path: '/ai/predictions',
          submenu: [
            { label: 'Energy Forecast', path: '/ai/energy-forecast' },
            { label: 'Weather Model', path: '/ai/weather-model' },
            { label: 'Performance', path: '/ai/performance' },
          ]
        },
        { id: 'anomaly', label: 'Anomaly Detection', icon: ExclamationTriangleIcon, path: '/ai/anomaly-detection', badge: 'AI' },
        { 
          id: 'ml-models', 
          label: 'ML Models', 
          icon: BeakerIcon, 
          path: '/ai/ml',
          submenu: [
            { label: 'Neural Networks', path: '/ai/ml/neural-networks' },
            { label: 'Time Series', path: '/ai/ml/time-series' },
            { label: 'Classification', path: '/ai/ml/classification' },
          ]
        },
        { 
          id: 'ai-analytics', 
          label: 'Analytics', 
          icon: DocumentChartBarIcon, 
          path: '/ai/analytics',
          submenu: [
            { label: 'Data Pipeline', path: '/ai/analytics/data-pipeline' },
            { label: 'Live Analytics', path: '/ai/analytics/live' },
            { label: 'Reports', path: '/ai/analytics/reports' },
          ]
        },
      ]
    },
    {
      title: 'System',
      items: [
        { id: 'equipment', label: 'Equipment', icon: BoltIcon, path: '/system/equipment' },
        { id: 'settings', label: 'Settings', icon: CogIcon, path: '/system/settings' },
        { id: 'ai-operator', label: 'AI Operator', icon: UserCircleIcon, path: '/system/ao', badge: 'AO' },
        { id: 'admin', label: 'Admin', icon: CogIcon, path: '/system/admin' },
        { id: 'systems-online', label: 'Systems Online', icon: CircleStackIcon, path: '/system/online', badge: 'Online' },
      ]
    }
  ];

  const toggleSubmenu = (itemId: string) => {
    setOpenSubmenu(openSubmenu === itemId ? null : itemId);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onMobileToggle && window.innerWidth < 1024) {
      onMobileToggle();
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
    if (onMobileToggle && window.innerWidth < 1024) {
      onMobileToggle();
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className={`fixed left-0 top-0 h-screen bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-50 transition-all duration-500 ease-out flex flex-col ${isCollapsed ? 'w-20' : 'w-72'} ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200/50 dark:border-gray-700/50">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-black dark:from-white dark:via-gray-200 dark:to-gray-300 flex items-center justify-center shadow-xl ring-1 ring-black/10 dark:ring-white/30">
            <BoltIcon className="w-5 h-5 text-white dark:text-gray-900" />
          </div>
          {!isCollapsed && (
            <div className="space-y-1">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                Rayzen AI
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                Operations Console
              </div>
              <div className="flex flex-wrap gap-1 text-[11px] font-semibold">
                <span className="px-2 py-0.5 rounded-full bg-gray-900 text-white">Operator</span>
                <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100">Admin</span>
                <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">Online</span>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isCollapsed && <ThemeToggle />}
          <button 
            onClick={onToggle} 
            className="p-2.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all duration-200 hidden lg:flex group"
          >
            {isCollapsed ? (
              <ChevronRightIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
            ) : (
              <ChevronLeftIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
            )}
          </button>
          <button 
            onClick={onMobileToggle} 
            className="p-2.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all duration-200 lg:hidden"
          >
            <Bars3Icon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        {navigationSections.map((section, sectionIndex) => (
          <div key={section.title} className="mb-8 animate-fade-in-up" style={{ animationDelay: `${sectionIndex * 100}ms` }}>
            {!isCollapsed && (
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 px-3">
                {section.title}
              </div>
            )}
            <div className="space-y-2">
              {section.items.map((item, itemIndex) => (
                <div key={item.id} className="animate-fade-in-up" style={{ animationDelay: `${(sectionIndex * 100) + (itemIndex * 50)}ms` }}>
                  <div
                    className={`group flex items-center gap-3 px-4 py-3 mx-1 rounded-2xl transition-all duration-300 cursor-pointer relative overflow-hidden ${
                      isActive(item.path) 
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 shadow-soft' 
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800/50'
                    }`}
                    onClick={() => {
                      if (item.submenu) {
                        toggleSubmenu(item.id);
                      } else {
                        handleNavigation(item.path);
                      }
                    }}
                  >
                    {/* Active Indicator */}
                    {isActive(item.path) && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-primary-500 to-primary-600 rounded-r-full" />
                    )}
                    
                    <div className={`w-6 h-6 flex items-center justify-center transition-all duration-300 ${
                      isActive(item.path) ? 'scale-110' : 'group-hover:scale-110'
                    }`}>
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                    </div>
                    
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 font-medium text-sm">{item.label}</span>
                        {item.badge && (
                          <span className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-all duration-300 ${
                            item.badge === 'Live' ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300' :
                            item.badge === 'AI' ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' :
                            item.badge === 'AO' ? 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300' :
                            item.badge === '24/7' ? 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                          }`}>
                            {item.badge}
                          </span>
                        )}
                        {item.submenu && (
                          <ChevronDownIcon 
                            className={`w-4 h-4 transition-all duration-300 ${
                              openSubmenu === item.id ? 'rotate-180 text-primary-500' : 'group-hover:text-primary-500'
                            }`} 
                          />
                        )}
                      </>
                    )}
                  </div>
                  
                  {item.submenu && !isCollapsed && openSubmenu === item.id && (
                    <div className="ml-10 mt-2 space-y-1 animate-slide-down">
                      {item.submenu.map((subItem) => (
                        <div 
                          key={subItem.path} 
                          className={`group flex items-center gap-3 px-4 py-2.5 mx-1 rounded-xl transition-all duration-300 cursor-pointer text-sm ${
                            isActive(subItem.path) 
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800/30'
                          }`}
                          onClick={() => handleNavigation(subItem.path)}
                        >
                          <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            isActive(subItem.path) 
                              ? 'bg-primary-500' 
                              : 'bg-gray-300 dark:bg-gray-600 group-hover:bg-primary-400'
                          }`} />
                          <span className="font-medium">{subItem.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200/60 dark:border-gray-700/60">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200"
        >
          <span className="flex items-center gap-3">
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            {!isCollapsed && <span className="font-semibold">Logout</span>}
          </span>
          {!isCollapsed && <span className="text-xs text-gray-500 dark:text-gray-400">Secure exit</span>}
        </button>
      </div>
    </div>
  );
};

export default AISidebar;
